// src/App.js
import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { initGA } from './utils/analytics';
import { prefetchImages } from './utils/imagePrefetch';
import GoogleAnalytics from './components/GoogleAnalytics';
import IndexNowIntegration from './components/IndexNowIntegration';
import ConversionHooks from './components/ConversionHooks';

// Layout + guards
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

// Keep home eager for fastest first paint
import Home from './pages/Home';

// Retry lazy import on ChunkLoadError (cache mismatch, network blip)
const lazyWithRetry = (importFn, retries = 2) =>
  lazy(async () => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await importFn();
      } catch (err) {
        const isChunkError = err?.name === 'ChunkLoadError' || err?.message?.includes('Loading chunk');
        if (attempt < retries && isChunkError) {
          await new Promise(r => setTimeout(r, 1000));
        } else {
          throw err;
        }
      }
    }
  });

// Lazy-load non-home routes to reduce initial bundle
const About = lazyWithRetry(() => import('./pages/About'));
const Photos = lazyWithRetry(() => import('./pages/Photos'));
const Films = lazyWithRetry(() => import('./pages/Films'));
const Packages = lazyWithRetry(() => import('./pages/Packages'));
const BudgetCalculator = lazyWithRetry(() => import('./pages/BudgetCalculator'));
const BookNow = lazyWithRetry(() => import('./pages/BookNow'));
const ThankYou = lazyWithRetry(() => import('./pages/ThankYou'));
const WriteForUs = lazyWithRetry(() => import('./pages/WriteForUs'));
const Blogs = lazyWithRetry(() => import('./pages/Blogs'));
const BlogPost = lazyWithRetry(() => import('./pages/BlogPost'));
const BlogLegacyRedirect = lazyWithRetry(() => import('./pages/BlogLegacyRedirect'));
const FAQ = lazyWithRetry(() => import('./pages/FAQ'));
const Refund = lazyWithRetry(() => import('./pages/Refund'));
const Privacy = lazyWithRetry(() => import('./pages/Privacy'));
const Terms = lazyWithRetry(() => import('./pages/Terms'));
const TestPage = lazyWithRetry(() => import('./pages/TestPage'));
const AdminLogin = lazyWithRetry(() => import('./pages/AdminLogin'));
const AdminDashboard = lazyWithRetry(() => import('./pages/AdminDashboard'));
const MigrateData = lazyWithRetry(() => import('./pages/MigrateData'));
const RangamatiWeddingPhotography = lazyWithRetry(() => import('./pages/RangamatiWeddingPhotography'));
const BandarbanWeddingPhotography = lazyWithRetry(() => import('./pages/BandarbanWeddingPhotography'));
const KhagrachhariWeddingPhotography = lazyWithRetry(() => import('./pages/KhagrachhariWeddingPhotography'));
const CoxBazarWeddingPhotography = lazyWithRetry(() => import('./pages/CoxBazarWeddingPhotography'));
const ChittagongWeddingPhotography = lazyWithRetry(() => import('./pages/ChittagongWeddingPhotography'));
const TrackDeliverables = lazyWithRetry(() => import('./pages/TrackDeliverables'));

function App() {
  useEffect(() => {
    initGA(); // ✅ Google Analytics
    prefetchImages(2500, 80); // Preload other page images in background after 2.5s
  }, []);

  return (
    <>
      <GoogleAnalytics />
      <IndexNowIntegration 
        autoSubmit={true}
        submitOnMount={true}
        showStatus={false}
      />
      <ConversionHooks />
      <Suspense fallback={
        <div className="app-loading" role="status" aria-live="polite">
          <span className="app-loading-spinner" aria-hidden />
          <span>Loading…</span>
        </div>
      }>
        <Routes>
        {/* Admin Routes - No Layout */}
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/migrate-data"
          element={
            <ProtectedRoute>
              <MigrateData />
            </ProtectedRoute>
          }
        />

        {/* Public Routes - With Layout */}
        <Route element={<Layout />}>
          {/* Root path shows Home */}
          <Route path="/" element={<Home />} />

          {/* Redirect /home → / */}
          <Route path="/home" element={<Navigate to="/" replace />} />

          {/* Other pages */}
          <Route path="/about" element={<About />} />
          <Route path="/photos" element={<Photos />} />
          <Route path="/films" element={<Films />} />
          <Route path="/packages" element={<Packages />} />
          <Route path="/budget-calculator" element={<BudgetCalculator />} />
          <Route path="/book" element={<BookNow />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/write-for-us" element={<WriteForUs />} />
          <Route path="/blog/:slug" element={<BlogLegacyRedirect />} />
          <Route path="/blogs/:slug" element={<BlogPost />} />
          <Route path="/blogs" element={<Blogs />} />

          {/* Location-specific pages */}
          <Route path="/rangamati-wedding-photography" element={<RangamatiWeddingPhotography />} />
          <Route path="/bandarban-wedding-photography" element={<BandarbanWeddingPhotography />} />
          <Route path="/khagrachhari-wedding-photography" element={<KhagrachhariWeddingPhotography />} />
          <Route path="/cox-bazar-wedding-photography" element={<CoxBazarWeddingPhotography />} />
          <Route path="/chittagong-wedding-photography" element={<ChittagongWeddingPhotography />} />

          <Route path="/track-deliverables" element={<TrackDeliverables />} />

          {/* Legal Pages */}
          <Route path="/faq" element={<FAQ />} />
          <Route path="/refund" element={<Refund />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />

          {/* Test Route */}
          <Route path="/test" element={<TestPage />} />

          {/* Redirect any unknown path to homepage */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
        </Routes>
      </Suspense>
    </>
  );
}

export default App;